import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn, getAvatarColor, getAvatarInitials } from "~/lib/utils";

const userAvatarVariants = cva(
  "relative flex shrink-0 overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      size: {
        xs: "h-4 w-4 text-[8px]",
        sm: "h-5 w-5 text-[9px]",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm",
        xl: "h-12 w-12 text-base",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const statusDotVariants = cva("absolute rounded-full border-2 border-white", {
  variants: {
    size: {
      xs: "h-1 w-1 bottom-0 right-0",
      sm: "h-1.5 w-1.5 bottom-0 right-0",
      md: "h-2 w-2 bottom-0 right-0",
      lg: "h-2.5 w-2.5 bottom-0 right-0",
      xl: "h-3 w-3 bottom-0 right-0",
    },
    status: {
      online: "bg-green-500",
      offline: "bg-gray-400",
      away: "bg-yellow-500",
    },
  },
});

export interface UserAvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof userAvatarVariants> {
  name: string;
  src?: string;
  status?: "online" | "offline" | "away";
}

const UserAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  UserAvatarProps
>(({ className, size, name, src, status, ...props }, ref) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const [glareOffsetX, setGlareOffsetX] = React.useState(0);
  const [glareOffsetY, setGlareOffsetY] = React.useState(0);

  const initials = getAvatarInitials(name);
  const bgColor = getAvatarColor(name);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = e.clientX - centerX;
    const y = e.clientY - centerY;

    const maxOffset = 30;
    const offsetX = Math.max(-maxOffset, Math.min(maxOffset, x));
    const offsetY = Math.max(-maxOffset, Math.min(maxOffset, y));
    setGlareOffsetX(offsetX);
    setGlareOffsetY(offsetY);
  };

  const embossOffsetX = isHovered ? glareOffsetX * 0.01 : 0;
  const embossOffsetY = isHovered ? glareOffsetY * 0.01 : 0;

  const letterHighlighting = isHovered
    ? `0 0 8px rgba(255,255,255,0.6), 0 0 12px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2)`
    : `0 0 4px rgba(255,255,255,0.3), 0 0 6px rgba(255,255,255,0.2)`;

  const embossingShadow = `${-0.25 + embossOffsetX}px ${-0.25 + embossOffsetY}px 0px rgba(255,255,255,0.8), ${0.25 - embossOffsetX}px ${0.25 - embossOffsetY}px 0px rgba(0,0,0,0.6), ${-0.5 + embossOffsetX}px ${-0.5 + embossOffsetY}px 1px rgba(0,0,0,0.8), ${0.5 - embossOffsetX}px ${0.5 - embossOffsetY}px 1px rgba(255,255,255,0.4), ${letterHighlighting}`;

  return (
    <>
      <svg width="0" height="0" className="absolute">
        <defs>
          <filter id="noise-filter" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              baseFrequency="0.9"
              numOctaves="4"
              seed="2"
              stitchTiles="stitch"
              type="fractalNoise"
            />
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncA type="discrete" tableValues="0.1 0.15 0.2 0.25" />
            </feComponentTransfer>
            <feComposite in2="SourceGraphic" operator="over" />
          </filter>
        </defs>
      </svg>

      <AvatarPrimitive.Root
        ref={ref}
        className={cn(userAvatarVariants({ size }), className)}
        role="img"
        aria-label={`Avatar for ${name}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setGlareOffsetX(0);
          setGlareOffsetY(0);
        }}
        onMouseMove={handleMouseMove}
        {...props}
      >
        <AvatarPrimitive.Image
          src={src}
          alt={`${name} avatar`}
          className="aspect-square h-full w-full"
        />
        <AvatarPrimitive.Fallback
          className={cn(
            "flex h-full w-full items-center justify-center rounded-full text-white font-semibold relative overflow-hidden",
            bgColor,
            "ring-1 ring-inset ring-white/40",
            "border-[0.25px] border-black/25",
            "before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/30 before:via-transparent before:to-black/20 before:pointer-events-none",
            isHovered &&
              "before:bg-gradient-to-b before:from-yellow-100/40 before:via-transparent before:to-black/10",
            "[filter:url(#noise-filter)]",
            "shadow-[inset_0_1px_2px_rgba(255,255,255,0.3),_inset_0_-1px_2px_rgba(0,0,0,0.2)]",
            isHovered &&
              "shadow-[inset_0_2px_4px_rgba(255,255,255,0.4),_inset_0_-2px_4px_rgba(0,0,0,0.3)]"
          )}
          style={{
            textShadow: embossingShadow,
          }}
        >
          {isHovered && (
            <div
              className="absolute inset-0 rounded-full transition-all duration-200 ease-out pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${50 + glareOffsetX}% ${50 + glareOffsetY}%, rgba(255,255,150,0.3) 0%, rgba(255,255,150,0.23) 15%, rgba(255,255,150,0.13) 35%, rgba(255,255,150,0.03) 55%, transparent 70%)`,
              }}
            />
          )}

          {isHovered && (
            <div
              className="absolute inset-0 rounded-full transition-all duration-250 ease-out pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${50 - glareOffsetX * 0.6}% ${50 - glareOffsetY * 0.6}%, rgba(255,255,200,0.1) 0%, rgba(255,255,200,0.05) 20%, transparent 40%)`,
              }}
            />
          )}

          {isHovered && (
            <div
              className="absolute inset-0 rounded-full opacity-13 transition-all duration-300 ease-out pointer-events-none"
              style={{
                background: `radial-gradient(ellipse ${90 + Math.abs(glareOffsetX)}% ${70 + Math.abs(glareOffsetY)}% at ${50 + glareOffsetX * 0.8}% ${50 + glareOffsetY * 0.8}%, rgba(255,255,180,0.2) 0%, rgba(255,255,180,0.07) 40%, transparent 60%)`,
              }}
            />
          )}

          {initials}
        </AvatarPrimitive.Fallback>

        {status && <div className={cn(statusDotVariants({ size, status }))} />}
      </AvatarPrimitive.Root>
    </>
  );
});

UserAvatar.displayName = "UserAvatar";

export { UserAvatar, userAvatarVariants };
